import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/contexts/TenantContext';

interface NavMenuItem {
  id: string;
  label: string;
  section_slug: string | null;
  external_url: string | null;
  sort_order: number;
  visible: boolean;
  menu_item_type: string;
  parent_id: string | null;
  projects?: { id: string; name: string; slug: string }[];
  children?: NavMenuItem[];
}

const ABOUT_MENU_ITEM: NavMenuItem = {
  id: 'about-page',
  label: 'About',
  section_slug: 'about',
  external_url: null,
  sort_order: Number.MAX_SAFE_INTEGER,
  visible: true,
  menu_item_type: 'about',
  parent_id: null,
};

interface SiteStyle {
  font_family: string;
  font_size: number;
  font_weight: number;
  text_color: string;
  link_color: string;
  hover_color: string;
  active_color: string;
  hover_transition: string;
  menu_position_h: string;
  menu_position_v: string;
  menu_position: string;
  logo_menu_gap: number;
  menu_type: string;
  text_transform: string;
  link_underline: boolean;
  link_hover_underline: boolean;
  link_bg_enabled: boolean;
  link_bg_color: string;
  site_name: string;
  logo_url: string | null;
  logo_position: string;
  logo_size: number;
  custom_font_url: string | null;
  menu_bg_enabled: boolean;
  menu_bg_color: string;
  menu_border_radius: number;
  menu_item_gap: number;
  site_margin: number;
  dark_mode_enabled: boolean;
  dark_bg_color: string;
  dark_text_color: string;
  dark_link_color: string;
  dark_hover_color: string;
  dark_active_color: string;
  dark_menu_bg_color: string;
  dark_link_bg_color: string;
  site_title: string;
  site_description: string;
  favicon_url: string | null;
  social_image_url: string | null;
  ga_measurement_id: string | null;
  footer_links: { label: string; url: string }[] | null;
}

const defaultStyle: SiteStyle = {
  font_family: 'IBM Plex Mono',
  font_size: 16,
  font_weight: 300,
  text_color: '#000000',
  link_color: '#0000FF',
  hover_color: '#0000FF',
  active_color: '#0000FF',
  hover_transition: 'quickflip',
  menu_position_h: 'central',
  menu_position_v: 'centro',
  menu_position: 'top-left',
  logo_menu_gap: 16,
  menu_type: 'horizontal',
  text_transform: 'none',
  link_underline: false,
  link_hover_underline: false,
  link_bg_enabled: false,
  link_bg_color: '#F7F6F2',
  site_name: '',
  logo_url: null,
  logo_position: 'top-right',
  logo_size: 80,
  custom_font_url: null,
  menu_bg_enabled: false,
  menu_bg_color: '#F7F6F2',
  menu_border_radius: 0,
  menu_item_gap: 8,
  site_margin: 0,
  dark_mode_enabled: false,
  dark_bg_color: '#1a1a1a',
  dark_text_color: '#F7F6F2',
  dark_link_color: '#6699ff',
  dark_hover_color: '#6699ff',
  dark_active_color: '#6699ff',
  dark_menu_bg_color: '#2a2a2a',
  dark_link_bg_color: '#2a2a2a',
  site_title: '',
  site_description: '',
  favicon_url: null,
  social_image_url: null,
  ga_measurement_id: null,
  footer_links: null,
};

const PUBLIC_SITE_SETTINGS_SELECT = `
  font_family,
  font_size,
  font_weight,
  text_color,
  link_color,
  hover_color,
  active_color,
  hover_transition,
  menu_position_h,
  menu_position_v,
  menu_position,
  logo_menu_gap,
  menu_type,
  text_transform,
  link_underline,
  link_hover_underline,
  link_bg_enabled,
  link_bg_color,
  site_name,
  logo_url,
  logo_position,
  logo_size,
  custom_font_url,
  menu_bg_enabled,
  menu_bg_color,
  menu_border_radius,
  menu_item_gap,
  site_margin,
  dark_mode_enabled,
  dark_bg_color,
  dark_text_color,
  dark_link_color,
  dark_hover_color,
  dark_active_color,
  dark_menu_bg_color,
  dark_link_bg_color,
  site_title,
  site_description,
  favicon_url,
  social_image_url,
  footer_links
`;

const LEGACY_POS_MAP: Record<string, string> = {
  'topo-esquerda': 'top-left', 'topo': 'top-center',
  'topo-direita': 'top-right', 'esquerda': 'center-left',
  'esquerda-central': 'center-left', 'esquerda-inferior': 'bottom-left',
  'centro': 'center', 'direita': 'center-right',
  'direita-inferior': 'bottom-right', 'bottom': 'bottom-center',
  'bottom-esquerda': 'bottom-left', 'bottom-direita': 'bottom-right',
};

const VALID_POS = new Set([
  'top-left', 'top-center', 'top-right',
  'center-left', 'center', 'center-right',
  'bottom-left', 'bottom-center', 'bottom-right',
]);

function normalizeMenuPosition(value: string | null | undefined, fallbackH?: string, fallbackV?: string): string {
  if (value && VALID_POS.has(value)) return value;
  if (value && LEGACY_POS_MAP[value]) return LEGACY_POS_MAP[value];
  if (fallbackV && LEGACY_POS_MAP[fallbackV]) return LEGACY_POS_MAP[fallbackV];
  if (fallbackH && LEGACY_POS_MAP[fallbackH]) return LEGACY_POS_MAP[fallbackH];
  return 'top-left';
}

const isDirectLink = (url: string) => /^(mailto:|tel:|sms:)/i.test(url.trim());

const openExternal = (url: string) => {
  if (isDirectLink(url)) {
    window.location.href = url.trim();
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

export function NavOverlay() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [menuItems, setMenuItems] = useState<NavMenuItem[]>([]);
  const [siteStyle, setSiteStyle] = useState<SiteStyle>(defaultStyle);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileHook = useIsMobile();
  const isMobile = isMobileHook ||
    (typeof navigator !== 'undefined' &&
     /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) ||
    (typeof window !== 'undefined' && window.innerWidth <= 768);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { username: tenantUsername, userId: tenantUserId } = useTenant();
  const { username: paramUsername } = useParams<{ username: string }>();
  const username = tenantUsername || paramUsername;
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(tenantUserId);

  useEffect(() => {
    if (tenantUserId) {
      setResolvedUserId(tenantUserId);
      return;
    }

    if (!username) {
      setResolvedUserId(null);
      return;
    }

    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc('resolve_username', { p_username: username });
      if (!cancelled) setResolvedUserId(data?.[0]?.user_id ?? null);
    })();

    return () => { cancelled = true; };
  }, [tenantUserId, username]);

  // Apply site margin, dark mode, global typography and CSS vars
  useEffect(() => {
    const root = document.getElementById('root');
    const docEl = document.documentElement;
    const dk = siteStyle.dark_mode_enabled;
    const textColor = dk ? siteStyle.dark_text_color : siteStyle.text_color;
    const linkColor = dk ? siteStyle.dark_link_color : siteStyle.link_color;
    const hoverColor = dk ? siteStyle.dark_hover_color : siteStyle.hover_color;
    const activeColor = dk ? siteStyle.dark_active_color : siteStyle.active_color;

    if (root) {
      root.style.padding = siteStyle.site_margin > 0 ? `${siteStyle.site_margin}px` : '0';
    }

    // Global typography
    document.body.style.fontFamily = `'${siteStyle.font_family}', 'Courier New', monospace`;
    document.body.style.fontSize = `${siteStyle.font_size}px`;
    document.body.style.fontWeight = String(siteStyle.font_weight);
    document.body.style.textTransform = siteStyle.text_transform as any;
    document.body.style.color = textColor;

    if (dk) {
      // Override CSS variables for dark mode
      const bgHex = siteStyle.dark_bg_color;
      const fgHex = siteStyle.dark_text_color;
      document.body.style.backgroundColor = bgHex;
      docEl.style.setProperty('--dark-bg', bgHex);
      docEl.style.setProperty('--dark-fg', fgHex);
      docEl.classList.add('dark-mode-active');
    } else {
      document.body.style.backgroundColor = '';
      docEl.classList.remove('dark-mode-active');
      docEl.style.removeProperty('--dark-bg');
      docEl.style.removeProperty('--dark-fg');
    }

    // CSS vars for links globally
    docEl.style.setProperty('--site-link-color', linkColor);
    docEl.style.setProperty('--site-hover-color', hoverColor);
    docEl.style.setProperty('--site-active-color', activeColor);

    return () => {
      if (root) root.style.padding = '0';
      document.body.style.fontFamily = '';
      document.body.style.fontSize = '';
      document.body.style.fontWeight = '';
      document.body.style.textTransform = '';
      document.body.style.color = '';
      document.body.style.backgroundColor = '';
      docEl.classList.remove('dark-mode-active');
      docEl.style.removeProperty('--dark-bg');
      docEl.style.removeProperty('--dark-fg');
      docEl.style.removeProperty('--site-link-color');
      docEl.style.removeProperty('--site-hover-color');
      docEl.style.removeProperty('--site-active-color');
    };
  }, [siteStyle]);

  // Load custom font if set
  useEffect(() => {
    if (siteStyle.custom_font_url) {
      const style = document.createElement('style');
      style.textContent = `
        @font-face {
          font-family: '${siteStyle.font_family}';
          src: url('${siteStyle.custom_font_url}') format('woff2'), url('${siteStyle.custom_font_url}') format('woff'), url('${siteStyle.custom_font_url}') format('truetype');
          font-display: swap;
        }
      `;
      document.head.appendChild(style);
      return () => { document.head.removeChild(style); };
    } else if (siteStyle.font_family !== 'IBM Plex Mono') {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${siteStyle.font_family.replace(/ /g, '+')}&display=swap`;
      document.head.appendChild(link);
      return () => { document.head.removeChild(link); };
    }
  }, [siteStyle.font_family, siteStyle.custom_font_url]);

  // SEO: Update title, meta tags, favicon, GA4
  useEffect(() => {
    // Title — use configured title, fallback to site_name, then username
    const fallbackTitle = siteStyle.site_name || (username ? username.toUpperCase() : '');
    document.title = siteStyle.site_title || fallbackTitle || 'Portfolio';

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    const fallbackDesc = fallbackTitle ? `${fallbackTitle} — Portfolio` : '';
    metaDesc.setAttribute('content', siteStyle.site_description || fallbackDesc);

    // Favicon
    let favicon = document.querySelector('link[rel="icon"]');
    if (siteStyle.favicon_url) {
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.setAttribute('rel', 'icon');
        document.head.appendChild(favicon);
      }
      favicon.setAttribute('href', siteStyle.favicon_url);
    }

    // OG tags
    const ogTitleContent = siteStyle.site_title || fallbackTitle || 'Portfolio';
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', ogTitleContent);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', siteStyle.site_description || fallbackDesc);
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage && siteStyle.social_image_url) ogImage.setAttribute('content', siteStyle.social_image_url);
    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (twitterImage && siteStyle.social_image_url) twitterImage.setAttribute('content', siteStyle.social_image_url);

    // GA4
    const existingGA = document.getElementById('ga4-script');
    if (siteStyle.ga_measurement_id && siteStyle.ga_measurement_id.trim().startsWith('G-')) {
      if (!existingGA) {
        const script = document.createElement('script');
        script.id = 'ga4-script';
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${siteStyle.ga_measurement_id}`;
        document.head.appendChild(script);

        const inlineScript = document.createElement('script');
        inlineScript.id = 'ga4-inline';
        inlineScript.textContent = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${siteStyle.ga_measurement_id}');
        `;
        document.head.appendChild(inlineScript);
      }
    } else if (existingGA) {
      existingGA.remove();
      const inlineGA = document.getElementById('ga4-inline');
      if (inlineGA) inlineGA.remove();
    }
  }, [siteStyle.site_title, siteStyle.site_description, siteStyle.favicon_url, siteStyle.social_image_url, siteStyle.ga_measurement_id]);

  // Independent useEffect for site_settings — reloads on appearance-saved event
  useEffect(() => {
    if (!resolvedUserId) return;
    const loadSettings = async () => {
      const { data } = await supabase
        .from('site_settings')
        .select(PUBLIC_SITE_SETTINGS_SELECT)
        .eq('user_id', resolvedUserId)
        .limit(1)
        .maybeSingle();
      if (data) {
        const raw = data as any;
        const normalized: SiteStyle = {
          ...defaultStyle,
          ...(raw as Partial<SiteStyle>),
          menu_position: normalizeMenuPosition(raw.menu_position, raw.menu_position_h, raw.menu_position_v),
          logo_menu_gap: typeof raw.logo_menu_gap === 'number' ? raw.logo_menu_gap : 16,
          ga_measurement_id: null,
        };
        setSiteStyle(normalized);
      }
    };
    loadSettings();
    const handleAppearanceUpdate = () => loadSettings();
    window.addEventListener('appearance-saved', handleAppearanceUpdate);
    return () => window.removeEventListener('appearance-saved', handleAppearanceUpdate);
  }, [resolvedUserId]);

  useEffect(() => {
    const load = async () => {
      if (!resolvedUserId) return;
      const uid = resolvedUserId;

      const { data: menuData } = await supabase
        .from('menu_items')
        .select('*')
        .eq('user_id', uid)
        .eq('visible', true)
        .order('sort_order');

      const { data: projects } = await supabase
        .from('projects')
        .select('id, name, slug, section_slug, published')
        .eq('user_id', uid)
        .eq('published', true)
        .order('sort_order');

      const { data: freePageData } = await supabase
        .from('free_pages')
        .select('slug, published')
        .eq('user_id', uid)
        .eq('published', true);

      const { data: aboutData } = await supabase
        .from('about_pages')
        .select('id')
        .eq('user_id', uid)
        .eq('status', 'published')
        .limit(1)
        .maybeSingle();

      const publishedPageSlugs = new Set((freePageData || []).map((fp: any) => fp.slug));

      // Build flat list first
      const allItems = ((menuData || []) as any[]).map((item: any) => ({
        ...item,
        menu_item_type: item.menu_item_type || 'section',
        parent_id: item.parent_id || null,
      })) as NavMenuItem[];

      // Build tree: top-level items with children, explicitly sorted by sort_order
      const topLevel = allItems.filter(i => !i.parent_id).sort((a, b) => a.sort_order - b.sort_order);
      const childMap = new Map<string, NavMenuItem[]>();
      allItems.filter(i => i.parent_id).forEach(child => {
        const list = childMap.get(child.parent_id!) || [];
        list.push(child);
        childMap.set(child.parent_id!, list);
      });
      // Sort children within each parent
      childMap.forEach((children, key) => {
        childMap.set(key, children.sort((a, b) => a.sort_order - b.sort_order));
      });

      const items: NavMenuItem[] = [];
      for (const item of topLevel) {
        const children = (childMap.get(item.id) || []).map(child => {
          if (child.menu_item_type === 'external') return child;
          if (child.menu_item_type === 'gallery' || child.menu_item_type === 'text' || child.menu_item_type === 'page') return child;
          // Legacy section type: show if has published projects
          if (child.section_slug) {
            const sectionProjects = (projects || []).filter(p => p.section_slug === child.section_slug);
            if (sectionProjects.length > 0) return { ...child, projects: sectionProjects.map(p => ({ id: p.id, name: p.name, slug: p.slug })) };
          }
          return null;
        }).filter(Boolean) as NavMenuItem[];

        if (item.menu_item_type === 'external') {
          items.push({ ...item, children: children.length > 0 ? children : undefined });
        } else if (item.menu_item_type === 'gallery' || item.menu_item_type === 'text' || item.menu_item_type === 'page') {
          items.push({ ...item, children: children.length > 0 ? children : undefined });
        } else if (item.menu_item_type === 'category') {
          if (children.length > 0) {
            items.push({ ...item, children });
          }
        } else if (item.section_slug) {
          // Legacy section type
          const sectionProjects = (projects || []).filter(p => p.section_slug === item.section_slug);
          if (sectionProjects.length > 0 || children.length > 0) {
            items.push({
              ...item,
              projects: sectionProjects.map(p => ({ id: p.id, name: p.name, slug: p.slug })),
              children: children.length > 0 ? children : undefined,
            });
          }
        }
      }
      if (aboutData && !items.some(item => item.section_slug === 'about')) {
        items.push(ABOUT_MENU_ITEM);
      }
      setMenuItems(items);
    };
    load();
  }, [resolvedUserId, username]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuClick = (item: NavMenuItem) => {
    if (item.external_url) {
      openExternal(item.external_url);
      if (isMobile) setMobileOpen(false);
      return;
    }
    // If item has children or projects, toggle dropdown
    if ((item.children && item.children.length > 0) || (item.projects && item.projects.length > 0)) {
      setOpenDropdown(openDropdown === item.id ? null : item.id);
      return;
    }
    // Gallery type — navigate to /g/slug
    if (item.menu_item_type === 'gallery' && item.section_slug) {
      navigate(`/g/${item.section_slug}`);
      if (isMobile) setMobileOpen(false);
      return;
    }
    // Page/text type — navigate to /p/slug
    if ((item.menu_item_type === 'page' || item.menu_item_type === 'text') && item.section_slug) {
      navigate(`/p/${item.section_slug}`);
      if (isMobile) setMobileOpen(false);
      return;
    }
    if (item.section_slug) {
      navigate(`/${item.section_slug}`);
      if (isMobile) setMobileOpen(false);
    }
  };

  const handleChildClick = (child: NavMenuItem) => {
    if (child.external_url) {
      openExternal(child.external_url);
    } else if (child.menu_item_type === 'gallery' && child.section_slug) {
      navigate(`/g/${child.section_slug}`);
    } else if ((child.menu_item_type === 'page' || child.menu_item_type === 'text') && child.section_slug) {
      navigate(`/p/${child.section_slug}`);
    } else if (child.section_slug) {
      navigate(`/${child.section_slug}`);
    }
    setOpenDropdown(null);
    if (isMobile) setMobileOpen(false);
  };

  const dk = siteStyle.dark_mode_enabled;
  const textColor = dk ? siteStyle.dark_text_color : siteStyle.text_color;
  const linkColor = dk ? siteStyle.dark_link_color : siteStyle.link_color;
  const hoverColor = dk ? siteStyle.dark_hover_color : siteStyle.hover_color;
  const activeColor = dk ? siteStyle.dark_active_color : siteStyle.active_color;

  const getTextAlign = (): 'left' | 'right' | 'center' => {
    const p = siteStyle.menu_position;
    if (p.endsWith('-left')) return 'left';
    if (p.endsWith('-right')) return 'right';
    return 'center';
  };

  const getAlignItems = (): 'flex-start' | 'flex-end' | 'center' => {
    const align = getTextAlign();
    if (align === 'left') return 'flex-start';
    if (align === 'right') return 'flex-end';
    return 'center';
  };

  const navStyle: React.CSSProperties = {
    fontFamily: `'${siteStyle.font_family}', 'Courier New', monospace`,
    fontSize: siteStyle.font_size,
    fontWeight: siteStyle.font_weight,
    color: textColor,
    WebkitFontSmoothing: 'antialiased' as any,
    MozOsxFontSmoothing: 'grayscale',
  };

  const linkBgStyle: React.CSSProperties = siteStyle.link_bg_enabled ? {
    backgroundColor: dk ? siteStyle.dark_link_bg_color : siteStyle.link_bg_color,
    padding: '4px 8px',
  } : {};

  const menuBgStyle: React.CSSProperties = siteStyle.menu_bg_enabled ? {
    backgroundColor: dk ? siteStyle.dark_menu_bg_color : siteStyle.menu_bg_color,
    padding: '16px 24px',
    borderRadius: siteStyle.menu_border_radius,
  } : {
    borderRadius: siteStyle.menu_border_radius,
  };

  const margin = siteStyle.site_margin;

  const getMenuPosition = (): React.CSSProperties => {
    const isVerticalMenu = siteStyle.menu_type === 'vertical';
    const POS_MAP: Record<string, React.CSSProperties> = {
      'top-left':      { top: 32 + margin, left: 32 + margin },
      'top-center':    { top: 32 + margin, left: '50%', transform: 'translateX(-50%)' },
      'top-right':     { top: 32 + margin, right: 32 + margin },
      'center-left':   { top: '50%', left: 32 + margin, transform: 'translateY(-50%)' },
      'center':        { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' },
      'center-right':  { top: '50%', right: 32 + margin, transform: 'translateY(-50%)' },
      'bottom-left':   { bottom: 32 + margin, left: 32 + margin },
      'bottom-center': { bottom: 32 + margin, left: '50%', transform: 'translateX(-50%)' },
      'bottom-right':  { bottom: 32 + margin, right: 32 + margin },
    };
    const base: React.CSSProperties = { position: 'fixed', zIndex: 100, ...POS_MAP[siteStyle.menu_position] };
    if (isVerticalMenu) base.flexDirection = 'column';

    // Logo/menu collision: if logo_position === menu_position, push the menu away.
    // For a text site name (no image logo), match the menu_item_gap so the space
    // between the site name and the first menu item equals the space between menu items.
    const hasIdentity = Boolean(siteStyle.logo_url || siteStyle.site_name?.trim());
    if (hasIdentity && siteStyle.logo_position === siteStyle.menu_position) {
      const identityHeight = siteStyle.logo_url
        ? (siteStyle.logo_size || 80)
        : Math.round((siteStyle.font_size || 16) * 1.5);
      const gap = siteStyle.logo_url
        ? (siteStyle.logo_menu_gap ?? 16)
        : (siteStyle.menu_item_gap ?? 8);
      const offset = identityHeight + gap;
      const p = siteStyle.menu_position;
      if (p.startsWith('top-')) {
        base.marginTop = offset;
      } else if (p.startsWith('bottom-')) {
        base.marginBottom = offset;
      } else {
        // center-* (lateral vertical) — push menu down beneath the logo
        base.marginTop = offset;
      }
    }

    return base;
  };


  const getLogoPosition = (): React.CSSProperties => {
    const pos: React.CSSProperties = { position: 'fixed', zIndex: 100 };
    switch (siteStyle.logo_position) {
      case 'top-left': pos.top = 32 + margin; pos.left = 32 + margin; break;
      case 'top-center': pos.top = 32 + margin; pos.left = '50%'; pos.transform = 'translateX(-50%)'; break;
      case 'bottom-left': pos.bottom = 32 + margin; pos.left = 32 + margin; break;
      case 'bottom-right': pos.bottom = 32 + margin; pos.right = 32 + margin; break;
      case 'bottom-center': pos.bottom = 32 + margin; pos.left = '50%'; pos.transform = 'translateX(-50%)'; break;
      case 'center-left': pos.top = '50%'; pos.left = 32 + margin; pos.transform = 'translateY(-50%)'; break;
      case 'center-right': pos.top = '50%'; pos.right = 32 + margin; pos.transform = 'translateY(-50%)'; break;
      default: pos.top = 32 + margin; pos.right = 32 + margin; // top-right
    }
    return pos;
  };

  // NavButton with vertical slide-up for quickflip
  const NavButton = ({
    label,
    onClick,
    onMouseEnter: onEnter,
    onMouseLeave: onLeave,
    isActive,
    style: extraStyle,
    isMobile: isMobileProp,
  }: {
    label: string;
    onClick: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    isActive?: boolean;
    style?: React.CSSProperties;
    isMobile?: boolean;
  }) => {
    const [hovered, setHovered] = useState(false);
    const t = siteStyle.hover_transition;

    const handleEnter = () => { setHovered(true); onEnter?.(); };
    const handleLeave = () => { setHovered(false); onLeave?.(); };

    if (t === 'quickflip' && !isMobileProp) {
      return (
        <div
          className="flip-container"
          style={{ cursor: 'pointer', color: textColor, ...linkBgStyle, ...extraStyle }}
          onClick={onClick}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <div className="flip-inner">
            <div className="flip-front" style={{
              ...navStyle,
              textDecoration: isActive ? 'underline' : (siteStyle.link_underline ? 'underline' : 'none'),
              textUnderlineOffset: 4,
              ...extraStyle,
            }}>
              {label}
            </div>
            <div className="flip-back" style={{ ...navStyle, color: hoverColor, ...extraStyle }}>
              {label}
            </div>
          </div>
        </div>
      );
    }

    // Build dynamic hover styles
    const getHoverStyles = (): React.CSSProperties => {
      const base: React.CSSProperties = {
        ...navStyle,
        ...linkBgStyle,
        cursor: 'pointer',
        textDecoration: isActive ? 'underline' : (siteStyle.link_underline ? 'underline' : 'none'),
        textUnderlineOffset: 4,
        display: 'inline-block',
        ...extraStyle,
      };

      const color = hovered ? hoverColor : textColor;

      switch (t) {
        case 'fade':
          return { ...base, color, transition: 'color 0.3s ease, opacity 0.3s ease', opacity: hovered ? 0.7 : 1 };
        case 'slide':
          return { ...base, color, transition: 'color 0.3s ease, transform 0.3s ease', transform: hovered ? 'translateX(4px)' : 'translateX(0)' };
        case 'scale':
          return { ...base, color, transition: 'color 0.3s ease, transform 0.3s ease', transform: hovered ? 'scale(1.1)' : 'scale(1)' };
        case 'blur':
          return { ...base, color, transition: 'color 0.3s ease, filter 0.3s ease', filter: hovered ? 'blur(1px)' : 'blur(0)' };
        case 'underline-slide':
          return { ...base, color, transition: 'color 0.3s ease', textDecoration: 'none', borderBottom: hovered ? `1px solid ${hoverColor}` : '1px solid transparent' };
        case 'color-wipe':
          return { ...base, transition: 'background-size 0.4s ease, color 0.3s ease', backgroundImage: `linear-gradient(${hoverColor}, ${hoverColor})`, backgroundRepeat: 'no-repeat', backgroundSize: hovered ? '100% 100%' : '0% 100%', color: hovered ? '#fff' : textColor, padding: '2px 6px' };
        case 'glow':
          return { ...base, color, transition: 'color 0.3s ease, text-shadow 0.3s ease', textShadow: hovered ? `0 0 8px ${hoverColor}` : 'none' };
        case 'letter-spacing':
          return { ...base, color, transition: 'color 0.3s ease, letter-spacing 0.3s ease', letterSpacing: hovered ? '3px' : '0' };
        case 'rotate':
          return { ...base, color, transition: 'color 0.3s ease, transform 0.3s ease', transform: hovered ? 'rotate(-3deg)' : 'rotate(0)' };
        case 'bounce':
          return { ...base, color, transition: 'color 0.3s ease, transform 0.3s cubic-bezier(0.68, -0.55, 0.27, 1.55)', transform: hovered ? 'translateY(-3px)' : 'translateY(0)' };
        case 'skew':
          return { ...base, color, transition: 'color 0.3s ease, transform 0.3s ease', transform: hovered ? 'skewX(-5deg)' : 'skewX(0)' };
        case 'shadow-lift':
          return { ...base, color, transition: 'color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease', transform: hovered ? 'translateY(-2px)' : 'translateY(0)', boxShadow: hovered ? `0 4px 12px ${hoverColor}40` : 'none', padding: '4px 8px' };
        case 'strike':
          return { ...base, color, transition: 'color 0.3s ease', textDecoration: hovered ? 'line-through' : (isActive ? 'underline' : 'none'), textDecorationColor: hoverColor };
        case 'background-slide':
          return { ...base, transition: 'background-position 0.4s ease, color 0.3s ease', backgroundImage: `linear-gradient(to right, ${hoverColor} 50%, transparent 50%)`, backgroundSize: '200% 100%', backgroundPosition: hovered ? '0% 0%' : '100% 0%', color: hovered ? '#fff' : textColor, padding: '4px 8px' };
        case 'opacity-scale':
          return { ...base, color, transition: 'color 0.3s ease, transform 0.3s ease, opacity 0.3s ease', transform: hovered ? 'scale(0.95)' : 'scale(1)', opacity: hovered ? 0.6 : 1 };
        default:
          return { ...base, color };
      }
    };

    return (
      <button
        onClick={onClick}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        className="bg-transparent"
        style={getHoverStyles()}
      >
        {label}
      </button>
    );
  };

  // Dropdown — supports both projects and children (subcategories)
  const Dropdown = ({ item }: { item: NavMenuItem }) => {
    const hasProjects = item.projects && item.projects.length > 0;
    const hasChildren = item.children && item.children.length > 0;
    if (openDropdown !== item.id || (!hasProjects && !hasChildren)) return null;

    return (
      <div
        style={{
          position: 'absolute',
          top: siteStyle.menu_type === 'vertical' ? 0 : '100%',
          left: siteStyle.menu_type === 'vertical' ? '100%' : '50%',
          transform: siteStyle.menu_type === 'vertical' ? 'translateX(8px)' : 'translateX(-50%)',
          padding: '16px 24px',
          backgroundColor: dk ? siteStyle.dark_bg_color : 'hsl(var(--background))',
          zIndex: 200,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          animation: 'dropdownIn 0.25s ease forwards',
          whiteSpace: 'nowrap',
        }}
      >
        {/* Render children (subcategories) first */}
        {hasChildren && item.children!.map(child => (
          <NavButton
            key={child.id}
            label={child.label}
            onClick={() => handleChildClick(child)}
            style={{ fontSize: 14 }}
          />
        ))}
        {/* Then render projects */}
        {hasProjects && item.projects!.map(p => (
          <NavButton
            key={p.id}
            label={p.name}
            onClick={() => { navigate(`/galeria/${p.slug}`); setOpenDropdown(null); if (isMobile) setMobileOpen(false); }}
            style={{ fontSize: 14 }}
          />
        ))}
      </div>
    );
  };

  // Site logo/name element — uses same font, color and hover transition as menu items
  const SiteIdentity = () => {
    const logoPos = getLogoPosition();
    if (siteStyle.logo_url) {
      return (
        <button
          onClick={() => navigate('/')}
          className="bg-transparent"
          style={{ ...logoPos, background: 'none', border: 'none', cursor: 'pointer', zIndex: 100 }}
        >
          <img src={siteStyle.logo_url} alt={siteStyle.site_name || ''} style={{ maxWidth: siteStyle.logo_size || 80, height: 'auto' }} className="object-contain" />
        </button>
      );
    }
    const displayName = siteStyle.site_name?.trim();
    if (!displayName) return null;
    return (
      <div style={{ ...logoPos }}>
        <NavButton
          label={displayName}
          onClick={() => navigate('/')}
        />
      </div>
    );
  };

  if (isMobile) {
    return (
      <>
        <div className="fixed top-0 left-0 right-0 z-[100] flex justify-between items-center" style={{ padding: 24 }}>
          <button onClick={() => { navigate('/'); setMobileOpen(false); }} className="bg-transparent" style={{ ...navStyle, background: 'none', border: 'none', cursor: 'pointer' }}>
            {siteStyle.logo_url
              ? <img src={siteStyle.logo_url} alt={siteStyle.site_name || ''} style={{ maxWidth: Math.min(siteStyle.logo_size || 80, 120), height: 'auto' }} className="object-contain" />
              : (siteStyle.site_name?.trim() || '')}
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="bg-transparent"
            style={{ ...navStyle, fontSize: 24, lineHeight: 1 }}
            aria-label="Menu"
          >
            {mobileOpen ? '×' : '+'}
          </button>
        </div>

        {mobileOpen && createPortal(
          <div className="fixed inset-0 z-[99] flex items-center justify-center" style={{ padding: '0 40px', backgroundColor: dk ? `${siteStyle.dark_bg_color}f5` : 'rgba(247,246,242,0.98)', color: dk ? siteStyle.dark_text_color : siteStyle.text_color }}>
            <nav className="flex flex-col items-center" style={{ gap: siteStyle.menu_item_gap ?? 24, color: dk ? siteStyle.dark_text_color : (siteStyle.text_color || '#0A0A0A') }} ref={dropdownRef}>

              {menuItems.map((item) => (
                <div key={item.id} className="flex flex-col items-center">
                  <NavButton
            label={item.label}
            onClick={() => handleMenuClick(item)}
            isActive={item.section_slug ? location.pathname.startsWith(`/${item.section_slug}`) : false}
            isMobile
                   />
                   {openDropdown === item.id && item.children?.map(child => (
                     <div key={child.id} className="mt-4">
                       <NavButton
                         label={child.label}
                        onClick={() => handleChildClick(child)}
                        style={{ fontSize: 14 }}
                        isMobile
                      />
                    </div>
                  ))}
                  {openDropdown === item.id && item.projects?.map(p => (
                    <div key={p.id} style={{ marginTop: 16 }}>
                      <NavButton
                        label={p.name}
                        onClick={() => { navigate(`/galeria/${p.slug}`); setOpenDropdown(null); setMobileOpen(false); }}
                        style={{ fontSize: 14 }}
                        isMobile
                      />
                    </div>
                  ))}
                </div>
              ))}

              {Array.isArray(siteStyle.footer_links) && siteStyle.footer_links.length > 0 && (
                <div
                  className="flex flex-col items-center"
                  style={{ marginTop: 40, gap: Math.max(8, Math.round((siteStyle.menu_item_gap ?? 24) / 2)) }}
                >
                  {siteStyle.footer_links.map((link) => (
                    <a
                      key={`${link.url}-${link.label}`}
                      href={link.url}
                      target={isDirectLink(link.url) ? undefined : '_blank'}
                      rel="noopener noreferrer"
                      onClick={() => setMobileOpen(false)}
                      style={{
                        ...navStyle,
                        textDecoration: 'none',
                        color: dk ? siteStyle.dark_text_color : (siteStyle.text_color || '#0A0A0A'),
                      }}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </nav>
          </div>,
          document.body
        )}
      </>
    );
  }

  const menuPos = getMenuPosition();

  return (
    <>
      <SiteIdentity />

      <nav
        className="flex"
        style={{ ...menuPos, ...menuBgStyle, gap: siteStyle.menu_item_gap ?? 8, display: 'flex', alignItems: getAlignItems() }}
        ref={dropdownRef}
      >
        {menuItems.map((item) => (
          <div key={item.id} style={{ position: 'relative' }}>
            <NavButton
              label={item.label}
              onClick={() => handleMenuClick(item)}
              isActive={item.section_slug ? location.pathname.startsWith(`/${item.section_slug}`) : false}
            />
            <Dropdown item={item} />
          </div>
        ))}
      </nav>
    </>
  );
}
